import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Badge, 
  Alert,
  Button,
  Spinner,
  Tabs,
  Tab,
  Modal,
  Form,
  Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faShare, 
  faDownload, 
  faTrash,
  faEllipsisV,
  faUser,
  faCalendarAlt,
  faMapMarkerAlt,
  faChartLine,
  faHome,
  faDollarSign,
  faCalculator
} from '@fortawesome/free-solid-svg-icons';
import SmartCMACharts from 'components/smart-cma/SmartCMACharts';
import PropertyMap from 'components/smart-cma/PropertyMap';
import PageBreadcrumb from 'components/common/PageBreadcrumb';

// Enum for different CMA report types
enum CMAReportType {
  SMART_CMA = 'SMART_CMA',
  MARKET_ANALYSIS = 'MARKET_ANALYSIS',
  PROPERTY_VALUATION = 'PROPERTY_VALUATION',
  INVESTMENT_ANALYSIS = 'INVESTMENT_ANALYSIS'
}

interface Report {
  id: string;
  title: string;
  reportType: CMAReportType;
  status: string;
  createdAt: string;
  updatedAt: string;
  propertyData: {
    address: string;
    sqft: number;
    bedrooms: number;
    bathrooms: number;
    yearBuilt: number;
    lotSize: number;
    garageSpaces: number;
    pool: boolean;
    city: string;
    postalCode: string;
    apn?: string;
  };
  predictionData: {
    value: number;
    confidence_score: number;
    value_range: {
      low: number;
      high: number;
    };
  };
  analysisData: any;
  comparablesData: any[];
  marketStats: {
    median_price: number;
    avg_price_per_sqft: number;
    avg_days_on_market: number;
    total_comparables: number;
    price_range: {
      min: number;
      max: number;
    };
  };
  user: {
    id: string;
    user_first_name: string;
    user_last_name: string;
    user_email: string;
  };
  lead?: {
    id: string;
    lead_first_name: string;
    lead_last_name: string;
    lead_email: string;
  };
  activities: any[];
}

const SmartCMAReportDetails: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modals
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Edit form
  const [editTitle, setEditTitle] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Get report type configuration
  const getReportTypeConfig = (reportType: CMAReportType) => {
    const configs = {
      [CMAReportType.SMART_CMA]: {
        name: 'Smart CMA',
        icon: faChartLine,
        color: 'primary',
        description: 'AI-powered Comparative Market Analysis'
      },
      [CMAReportType.MARKET_ANALYSIS]: {
        name: 'Market Analysis',
        icon: faHome,
        color: 'info',
        description: 'Comprehensive Market Study'
      },
      [CMAReportType.PROPERTY_VALUATION]: {
        name: 'Property Valuation',
        icon: faDollarSign,
        color: 'success',
        description: 'Professional Property Appraisal'
      },
      [CMAReportType.INVESTMENT_ANALYSIS]: {
        name: 'Investment Analysis',
        icon: faCalculator,
        color: 'warning',
        description: 'Investment ROI Assessment'
      }
    };
    return configs[reportType] || configs[CMAReportType.SMART_CMA];
  };

  // Fetch report details
  const fetchReport = async () => {
    if (!reportId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/smart-cma/${reportId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const data = await response.json();
      
      if (data.success) {
        setReport(data.data);
        setEditTitle(data.data.title);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  // Update report
  const updateReport = async () => {
    if (!reportId || !editTitle.trim()) return;
    
    setIsUpdating(true);
    try {
      const response = await fetch(`/api/smart-cma/${reportId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: editTitle })
      });

      if (response.ok) {
        await fetchReport(); // Refresh data
        setShowEditModal(false);
      } else {
        throw new Error('Failed to update report');
      }
    } catch (err) {
      alert('Error updating report: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete report
  const deleteReport = async () => {
    if (!reportId) return;
    
    try {
      const response = await fetch(`/api/smart-cma/${reportId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        navigate('/smart-cma-reports');
      } else {
        throw new Error('Failed to delete report');
      }
    } catch (err) {
      alert('Error deleting report: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Share report
  const shareReport = async () => {
    if (!reportId) return;
    
    try {
      const response = await fetch(`/api/smart-cma/${reportId}/share`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessLevel: 'VIEW_ONLY',
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
        })
      });

      if (response.ok) {
        const data = await response.json();
        navigator.clipboard.writeText(data.data.shareUrl);
        alert('Share link copied to clipboard!');
        setShowShareModal(false);
      } else {
        throw new Error('Failed to share report');
      }
    } catch (err) {
      alert('Error sharing report: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  // Format functions
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      'COMPLETED': 'success',
      'DRAFT': 'warning',
      'GENERATING': 'info',
      'ERROR': 'danger',
      'PUBLISHED': 'primary',
      'ARCHIVED': 'secondary'
    };
    
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'CRM', url: '/' },
    { label: 'CMA Reports', url: '/smart-cma-reports' },
    { label: report?.title || 'Report Details', url: '', active: true }
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2 text-muted">Loading report...</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <h5>Error Loading Report</h5>
          <p>{error || 'Report not found'}</p>
          <Button variant="outline-danger" onClick={() => navigate('/smart-cma-reports')}>
            Back to Reports
          </Button>
        </Alert>
      </div>
    );
  }

  const reportTypeConfig = getReportTypeConfig(report.reportType);

  // Property Summary Card - Dynamic based on report type
  const PropertySummaryCard = () => (
    <Card className="mb-4">
      <Card.Body>
        <Row>
          <Col md={8}>
            <div className="d-flex align-items-center mb-3">
              <div className={`bg-${reportTypeConfig.color} text-white rounded-circle d-flex align-items-center justify-content-center me-3`} 
                   style={{ width: '48px', height: '48px' }}>
                <FontAwesomeIcon icon={reportTypeConfig.icon} size="lg" />
              </div>
              <div>
                <h4 className="mb-1">{report.propertyData.address}</h4>
                <p className="text-muted mb-0">{reportTypeConfig.description}</p>
              </div>
            </div>
            <Row className="g-3">
              <Col sm={6}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Bedrooms:</span>
                  <strong>{report.propertyData.bedrooms}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Bathrooms:</span>
                  <strong>{report.propertyData.bathrooms}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Square Feet:</span>
                  <strong>{report.propertyData.sqft.toLocaleString()}</strong>
                </div>
              </Col>
              <Col sm={6}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Year Built:</span>
                  <strong>{report.propertyData.yearBuilt}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Lot Size:</span>
                  <strong>{report.propertyData.lotSize.toLocaleString()} sq ft</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Pool:</span>
                  <strong>{report.propertyData.pool ? 'Yes' : 'No'}</strong>
                </div>
              </Col>
            </Row>
          </Col>
          <Col md={4} className="text-md-end">
            <h2 className={`text-${reportTypeConfig.color} mb-1`}>{formatCurrency(report.predictionData.value)}</h2>
            <p className="text-muted mb-2">
              {report.reportType === CMAReportType.SMART_CMA && 'AI Predicted Value'}
              {report.reportType === CMAReportType.MARKET_ANALYSIS && 'Market Value'}
              {report.reportType === CMAReportType.PROPERTY_VALUATION && 'Appraised Value'}
              {report.reportType === CMAReportType.INVESTMENT_ANALYSIS && 'Investment Value'}
            </p>
            <Badge bg={reportTypeConfig.color} className="mb-2">
              {(report.predictionData.confidence_score * 100).toFixed(0)}% Confidence
            </Badge>
            <div className="small text-muted">
              Range: {formatCurrency(report.predictionData.value_range.low)} - {formatCurrency(report.predictionData.value_range.high)}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // Market Stats Card
  const MarketStatsCard = () => (
    <Card className="mb-4">
      <Card.Body>
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faChartLine} className="me-2" />
          Market Statistics
        </h5>
        <Row className="g-3">
          <Col md={3}>
            <div className="text-center p-3 bg-light rounded">
              <h6 className="text-primary mb-1">{formatCurrency(report.marketStats.median_price)}</h6>
              <small className="text-muted">Median Price</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-3 bg-light rounded">
              <h6 className="text-info mb-1">${report.marketStats.avg_price_per_sqft}</h6>
              <small className="text-muted">Price per Sq Ft</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-3 bg-light rounded">
              <h6 className="text-warning mb-1">{report.marketStats.avg_days_on_market}</h6>
              <small className="text-muted">Avg Days on Market</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-3 bg-light rounded">
              <h6 className="text-success mb-1">{report.marketStats.total_comparables}</h6>
              <small className="text-muted">Comparables</small>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // Comparables Grid
  const ComparablesGrid = () => (
    <Row className="g-3">
      {report.comparablesData.slice(0, 6).map((comp: any, index: number) => {
        const priceVariance = ((comp.price - report.predictionData.value) / report.predictionData.value * 100);
        
        return (
          <Col lg={4} md={6} key={comp.id || index}>
            <Card className="h-100">
              <div className="position-relative">
                {comp.photos && comp.photos.length > 0 ? (
                  <img 
                    src={comp.photos[0]} 
                    className="card-img-top" 
                    style={{ height: '200px', objectFit: 'cover' }}
                    alt={comp.address}
                  />
                ) : (
                  <div 
                    className="card-img-top d-flex align-items-center justify-content-center bg-light text-muted"
                    style={{ height: '200px' }}
                  >
                    No Image Available
                  </div>
                )}
                <Badge 
                  bg={priceVariance > 0 ? 'danger' : 'success'}
                  className="position-absolute top-0 end-0 m-2"
                >
                  {priceVariance > 0 ? '+' : ''}{priceVariance.toFixed(1)}%
                </Badge>
              </div>
              <Card.Body className="p-3">
                <h6 className="card-title text-truncate">{comp.address}</h6>
                <h5 className="text-primary mb-2">{formatCurrency(comp.price)}</h5>
                <div className="small text-muted mb-2">
                  <div>{comp.bedrooms}bd • {comp.bathrooms}ba • {comp.square_feet?.toLocaleString()}sf</div>
                  <div>Built: {comp.year_built} • Lot: {comp.lot_size?.toLocaleString()}sf</div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-success fw-bold">
                    ${comp.price_per_sqft ? comp.price_per_sqft.toFixed(0) : Math.round(comp.price / comp.square_feet)}/sf
                  </small>
                  <small className="text-muted">
                    {comp.days_on_market} days
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  // Get tabs based on report type
  const getTabsForReportType = (reportType: CMAReportType) => {
    const baseTabs = [
      { key: 'overview', title: 'Property Overview', icon: faHome },
      { key: 'comparables', title: 'Comparables', icon: faChartLine }
    ];

    const typeSpecificTabs = {
      [CMAReportType.SMART_CMA]: [
        { key: 'analytics', title: 'AI Analytics', icon: faCalculator },
        { key: 'map', title: 'Market Map', icon: faMapMarkerAlt }
      ],
      [CMAReportType.MARKET_ANALYSIS]: [
        { key: 'trends', title: 'Market Trends', icon: faChartLine },
        { key: 'demographics', title: 'Demographics', icon: faUser }
      ],
      [CMAReportType.PROPERTY_VALUATION]: [
        { key: 'valuation', title: 'Valuation Details', icon: faDollarSign },
        { key: 'adjustments', title: 'Price Adjustments', icon: faCalculator }
      ],
      [CMAReportType.INVESTMENT_ANALYSIS]: [
        { key: 'roi', title: 'ROI Analysis', icon: faCalculator },
        { key: 'projections', title: 'Projections', icon: faChartLine }
      ]
    };

    const activityTab = { key: 'activity', title: 'Activity Log', icon: faCalendarAlt };
    
    return [...baseTabs, ...typeSpecificTabs[reportType], activityTab];
  };

  // Render tab content based on report type and active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <PropertyOverviewContent />;
      case 'comparables':
        return <ComparablesGrid />;
      case 'analytics':
        return report.reportType === CMAReportType.SMART_CMA ? <SmartAnalyticsContent /> : null;
      case 'map':
        return <MapContent />;
      case 'trends':
        return report.reportType === CMAReportType.MARKET_ANALYSIS ? <MarketTrendsContent /> : null;
      case 'demographics':
        return report.reportType === CMAReportType.MARKET_ANALYSIS ? <DemographicsContent /> : null;
      case 'valuation':
        return report.reportType === CMAReportType.PROPERTY_VALUATION ? <ValuationDetailsContent /> : null;
      case 'adjustments':
        return report.reportType === CMAReportType.PROPERTY_VALUATION ? <PriceAdjustmentsContent /> : null;
      case 'roi':
        return report.reportType === CMAReportType.INVESTMENT_ANALYSIS ? <ROIAnalysisContent /> : null;
      case 'projections':
        return report.reportType === CMAReportType.INVESTMENT_ANALYSIS ? <ProjectionsContent /> : null;
      case 'activity':
        return <ActivityContent />;
      default:
        return <PropertyOverviewContent />;
    }
  };

  // Property Overview Content
  const PropertyOverviewContent = () => (
    <Row className="g-4">
      <Col md={6}>
        <Card>
          <Card.Header>
            <h6 className="mb-0">Property Details</h6>
          </Card.Header>
          <Card.Body>
            <div className="property-details">
              <div className="row g-2">
                <div className="col-6">
                  <small className="text-muted">Type:</small>
                  <div className="fw-bold">Residential</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Style:</small>
                  <div className="fw-bold">Single Family</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Garage:</small>
                  <div className="fw-bold">{report.propertyData.garageSpaces} spaces</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Pool:</small>
                  <div className="fw-bold">{report.propertyData.pool ? 'Yes' : 'No'}</div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6}>
        <Card>
          <Card.Header>
            <h6 className="mb-0">Location Details</h6>
          </Card.Header>
          <Card.Body>
            <div className="location-details">
              <div className="mb-2">
                <small className="text-muted">Full Address:</small>
                <div className="fw-bold">{report.propertyData.address}</div>
              </div>
              <div className="mb-2">
                <small className="text-muted">City:</small>
                <div className="fw-bold">{report.propertyData.city}</div>
              </div>
              <div className="mb-2">
                <small className="text-muted">ZIP Code:</small>
                <div className="fw-bold">{report.propertyData.postalCode}</div>
              </div>
              {report.propertyData.apn && (
                <div className="mb-2">
                  <small className="text-muted">APN:</small>
                  <div className="fw-bold">{report.propertyData.apn}</div>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // Smart Analytics Content (for Smart CMA reports)
  const SmartAnalyticsContent = () => (
    <SmartCMACharts 
      predictionData={report.predictionData}
      marketData={{
        feature_importance: [
          { name: "Location", importance: 0.35, value: report.propertyData.address },
          { name: "Square Footage", importance: 0.25, value: `${report.propertyData.sqft} sq ft` },
          { name: "Age", importance: 0.15, value: `${new Date().getFullYear() - report.propertyData.yearBuilt} years` },
          { name: "Bedrooms", importance: 0.12, value: `${report.propertyData.bedrooms} beds` },
          { name: "Bathrooms", importance: 0.08, value: `${report.propertyData.bathrooms} baths` },
          { name: "Lot Size", importance: 0.05, value: `${report.propertyData.lotSize} sq ft` }
        ],
        price_distribution: {
          price_ranges: [
            `$${Math.round(report.marketStats.price_range.min / 1000)}k - $${Math.round(report.marketStats.median_price * 0.8 / 1000)}k`,
            `$${Math.round(report.marketStats.median_price * 0.8 / 1000)}k - $${Math.round(report.marketStats.median_price / 1000)}k`,
            `$${Math.round(report.marketStats.median_price / 1000)}k - $${Math.round(report.marketStats.median_price * 1.2 / 1000)}k (Your Property)`,
            `$${Math.round(report.marketStats.median_price * 1.2 / 1000)}k - $${Math.round(report.marketStats.price_range.max / 1000)}k`
          ],
          counts: [
            Math.round(report.marketStats.total_comparables * 0.25),
            Math.round(report.marketStats.total_comparables * 0.35),
            Math.round(report.marketStats.total_comparables * 0.25),
            Math.round(report.marketStats.total_comparables * 0.15)
          ]
        }
      }}
    />
  );

  // Map Content
  const MapContent = () => (
    <PropertyMap
      subjectProperty={{
        address: report.propertyData.address,
        prediction_value: report.predictionData.value
      }}
      comparables={report.comparablesData.slice(0, 10).map((comp: any) => ({
        id: comp.id || Math.random().toString(),
        address: comp.address,
        price: comp.price,
        bedrooms: comp.bedrooms,
        bathrooms: comp.bathrooms,
        square_feet: comp.square_feet,
        status: comp.status || 'sold'
      }))}
    />
  );

  // Market Trends Content (for Market Analysis reports)
  const MarketTrendsContent = () => (
    <Alert variant="info">
      <h6>Market Trends Analysis</h6>
      <p>Detailed market trends analysis would be displayed here for Market Analysis reports.</p>
    </Alert>
  );

  // Demographics Content (for Market Analysis reports)
  const DemographicsContent = () => (
    <Alert variant="info">
      <h6>Demographics Analysis</h6>
      <p>Detailed demographics analysis would be displayed here for Market Analysis reports.</p>
    </Alert>
  );

  // Valuation Details Content (for Property Valuation reports)
  const ValuationDetailsContent = () => (
    <Alert variant="success">
      <h6>Professional Valuation Details</h6>
      <p>Detailed property valuation methodology and findings would be displayed here.</p>
    </Alert>
  );

  // Price Adjustments Content (for Property Valuation reports)
  const PriceAdjustmentsContent = () => (
    <Alert variant="success">
      <h6>Price Adjustments</h6>
      <p>Detailed price adjustments and comparables analysis would be displayed here.</p>
    </Alert>
  );

  // ROI Analysis Content (for Investment Analysis reports)
  const ROIAnalysisContent = () => (
    <Alert variant="warning">
      <h6>ROI Analysis</h6>
      <p>Detailed return on investment analysis would be displayed here for Investment Analysis reports.</p>
    </Alert>
  );

  // Projections Content (for Investment Analysis reports)
  const ProjectionsContent = () => (
    <Alert variant="warning">
      <h6>Investment Projections</h6>
      <p>Detailed investment projections and forecasts would be displayed here.</p>
    </Alert>
  );

  // Activity Content
  const ActivityContent = () => (
    <div className="timeline">
      {report.activities.map((activity: any, index: number) => (
        <div key={index} className="d-flex mb-3">
          <div className="flex-shrink-0">
            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
              <small>{index + 1}</small>
            </div>
          </div>
          <div className="flex-grow-1 ms-3">
            <div className="d-flex justify-content-between">
              <strong>{activity.description}</strong>
              <small className="text-muted">{formatDate(activity.createdAt)}</small>
            </div>
            <small className="text-muted">
              by {activity.user?.user_first_name} {activity.user?.user_last_name}
            </small>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="container-fluid">
      <PageBreadcrumb items={breadcrumbItems} />
      
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            className="me-3"
            onClick={() => navigate('/smart-cma-reports')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to CMA Reports
          </Button>
          <div>
            <div className="d-flex align-items-center mb-1">
              <Badge bg={reportTypeConfig.color} className="me-2">
                {reportTypeConfig.name}
              </Badge>
              <h2 className="mb-0">{report.title}</h2>
            </div>
            <div className="d-flex align-items-center gap-3 text-muted">
              <span>
                <FontAwesomeIcon icon={faUser} className="me-1" />
                {report.user.user_first_name} {report.user.user_last_name}
              </span>
              <span>
                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                {formatDate(report.createdAt)}
              </span>
              {getStatusBadge(report.status)}
            </div>
          </div>
        </div>
        
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => setShowEditModal(true)}>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit
          </Button>
          <Button variant="outline-info" onClick={() => setShowShareModal(true)}>
            <FontAwesomeIcon icon={faShare} className="me-2" />
            Share
          </Button>
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              <FontAwesomeIcon icon={faEllipsisV} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => window.print()}>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Export PDF
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item 
                className="text-danger" 
                onClick={() => setShowDeleteModal(true)}
              >
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Delete Report
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Report Content */}
      <PropertySummaryCard />
      <MarketStatsCard />

      {/* Tabbed Content */}
      <Card>
        <Card.Body>
          <Tabs 
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "overview")}
            className="mb-4"
          >
            {getTabsForReportType(report.reportType).map((tab) => (
              <Tab key={tab.key} eventKey={tab.key} title={tab.title}>
                {renderTabContent()}
              </Tab>
            ))}
          </Tabs>
        </Card.Body>
      </Card>

      {/* Modals */}
      
      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Report Title</Form.Label>
              <Form.Control
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter report title"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={updateReport}
            disabled={isUpdating || !editTitle.trim()}
          >
            {isUpdating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              'Update Report'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Share Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Share Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Generate a secure share link for this report. The link will be valid for 30 days and allow view-only access.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={shareReport}>
            Generate Share Link
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this report?</p>
          <p><strong>{report.title}</strong></p>
          <p className="text-muted">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteReport}>
            Delete Report
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SmartCMAReportDetails; 